<template>
	<Pupup :width="800" class="uploader" @close="close" :shuffleUp="true">
		<template v-slot:content>
			<div class="uploader-content">
				<div id="file-drag-drop">
					<form ref="fileform" :class="[{ drag: dragAndDropCapable, form: true }]">
						<div v-if="uploadPercentage === 0">
							<div class="drop-files" v-if="uploadPercentage === 0 && dragAndDropCapable">{{ getDragText }}</div>
							<label
								>{{ uploadLabel }}
								<input
									type="file"
									id="file"
									ref="fileInput"
									@change="handleFileUpload(false)"
									:multiple="multiple"
									:accept="acceptedFileTypes" />
							</label>
						</div>
						<p
							v-if="uploadPercentage > 0"
							v-html="uploadPercentage + '% complete' + (uploadPercentage === 100 ? ' - Processing...' : '')"
							class="progress-text" />
						<div class="progress" v-if="uploadPercentage > 0">
							<span :style="{ width: uploadPercentage + '%' }" />
						</div>
					</form>

					<div v-if="multiple && preview">
						<div v-for="(file, key) in files" :key="key" class="file-listing">
							<img
								class="preview"
								:ref="
									(el) => {
										if (el) previewRefs[key] = el;
									}
								" />
							{{ file.name }}
							<span class="remove" @click="removeFile(key)">Remove</span>
						</div>
					</div>

					<span class="large-button" @click="handleFileUpload(true)" v-show="files.length > 0" v-if="multiple && preview">Upload</span>
				</div>
			</div>
		</template>
	</Pupup>
</template>

<script>
import { ref, computed, onMounted, nextTick } from "vue";
import ApiService from "./../services/api.service";
import Pupup from "./Display/Pupup.vue";

export default {
	name: "Uploader",
	components: { Pupup },
	props: {
		imagesOnly: {
			type: Boolean,
			default: false,
		},
		multiple: {
			type: Boolean,
			default: false,
		},
		dragText: {
			type: String,
			default: "",
		},
		title: {
			type: String,
			default: "",
		},
		fileTypes: {
			type: Array,
			default: () => [],
		},
		uploadHandler: {
			type: String,
			default: "",
		},
		formData: {
			type: Object,
			default: () => ({}),
		},
		action: {
			type: String,
			default: "upload", // upload, emit
		},
		preview: {
			type: Boolean,
			default: true,
		},
		uploadURI: {
			type: String,
			default: "upload",
		},
	},
	emits: ["close-uploader", "started-upload", "finished-upload"],
	setup(props, { emit }) {
		// --- Template Refs ---
		const fileform = ref(null);
		const fileInput = ref(null);
		const previewRefs = ref([]);

		// --- State ---
		const dragAndDropCapable = ref(false);
		const files = ref([]);
		const uploadPercentage = ref(0);
		const uploadLabel = ref("");

		// --- Computed ---
		const acceptedFileTypes = computed(() => {
			if (props.fileTypes.length > 0) return props.fileTypes.join(",");
			if (props.imagesOnly) return ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"].join(",");
			return null;
		});

		const getDragText = computed(() => {
			if (props.dragText !== "") return props.dragText;
			return props.multiple
				? props.imagesOnly
					? "Drag your images here"
					: "Drag your files here"
				: props.imagesOnly
					? "Drag your image here"
					: "Drag your file here";
		});

		const getTitle = computed(() => {
			if (props.title !== "") return props.title;
			return props.multiple ? (props.imagesOnly ? "Upload images" : "Upload files") : props.imagesOnly ? "Upload an image" : "Upload a file";
		});

		// --- Methods ---
		const determineDragAndDropCapable = () => {
			const div = document.createElement("div");
			return ("draggable" in div || ("ondragstart" in div && "ondrop" in div)) && "FormData" in window && "FileReader" in window;
		};

		const close = () => {
			emit("close-uploader");
		};

		const isValidFileType = (file) => {
			if (props.fileTypes.length === 0) return true;
			const fileExtension = "." + file.name.split(".").pop().toLowerCase();
			return props.fileTypes.some((type) => type.toLowerCase() === fileExtension);
		};

		const submitFiles = () => {
			let formData = new FormData();
			let vars = [];

			for (let i = 0; i < files.value.length; i++) {
				let file = files.value[i];
				formData.append("files[" + i + "]", file);
			}

			Object.keys(props.formData).forEach((formDataField) => {
				if (formDataField === "vars") {
					Object.keys(props.formData.vars).forEach((varsField) => {
						formData.set(varsField, props.formData.vars[varsField]);
						vars.push(varsField);
					});
					formData.set("vars", vars.join(","));
				} else {
					formData.set(formDataField, props.formData[formDataField]);
				}
			});

			if (props.multiple === false && props.action === "emit") {
				let reader = new FileReader();
				reader.addEventListener(
					"load",
					() => {
						emit("close-uploader", { files: [{ fileName: reader.result }] });
					},
					false
				);
				reader.readAsDataURL(files.value[0]);
				return;
			}

			emit("started-upload");
			ApiService.post(props.uploadURI, formData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
				onUploadProgress: (progressEvent) => {
					uploadPercentage.value = parseInt(Math.round((progressEvent.loaded * 100) / progressEvent.total));
				},
			})
				.then((data) => {
					emit("finished-upload");
					emit("close-uploader", data.data);
				})
				.catch((err) => {
					emit("finished-upload");
					const errorMsg = err.response?.data?.error || err.response?.statusText || "There was an error uploading your file";
					emit("close-uploader", { error: errorMsg });
				});
		};

		const getImagePreviews = () => {
			for (let i = 0; i < files.value.length; i++) {
				if (/\.(jpe?g|png|gif|webp|svg)$/i.test(files.value[i].name)) {
					let reader = new FileReader();
					reader.addEventListener(
						"load",
						() => {
							if (previewRefs.value[i]) {
								previewRefs.value[i].src = reader.result;
							}
						},
						false
					);
					reader.readAsDataURL(files.value[i]);
				} else {
					nextTick(() => {
						if (previewRefs.value[i]) {
							previewRefs.value[i].src = "/images/file.png";
						}
					});
				}
			}
		};

		const handleFileUpload = (submit) => {
			const uploads = fileInput.value.files;
			const newFiles = props.multiple ? uploads : [uploads[0]];

			for (let i = 0; i < newFiles.length; i++) {
				if (isValidFileType(newFiles[i])) {
					files.value.push(newFiles[i]);
				} else {
					console.warn(`File "${newFiles[i].name}" is not of an accepted type and was skipped.`);
				}
			}

			// Reset the input so @change fires for the same file
			if (fileInput.value) fileInput.value.value = "";

			if (props.uploadHandler) {
				// This relies on a method with this name existing on the parent, which is not a good pattern.
				// For migration, we assume this is handled by the parent component.
				// A better pattern would be to emit an event.
			} else if (props.multiple && props.imagesOnly && typeof submit !== "undefined" && props.preview) {
				getImagePreviews();
			} else {
				submitFiles();
			}
		};

		const removeFile = (key) => {
			files.value.splice(key, 1);
		};

		// --- Lifecycle ---
		onMounted(() => {
			dragAndDropCapable.value = determineDragAndDropCapable();
			if (!dragAndDropCapable.value) return;
			uploadLabel.value = props.multiple ? "Or select them here - " : "Or select it here - ";
			if (!fileform.value) return;

			["drag", "dragstart", "dragend", "dragover", "dragenter", "dragleave", "drop"].forEach((evt) => {
				fileform.value.addEventListener(
					evt,
					(e) => {
						e.preventDefault();
						e.stopPropagation();
					},
					false
				);
			});

			fileform.value.addEventListener("drop", (e) => {
				const droppedFiles = e.dataTransfer.files;
				for (let i = 0; i < droppedFiles.length; i++) {
					if (isValidFileType(droppedFiles[i])) {
						files.value.push(droppedFiles[i]);
						if (props.multiple && props.preview) {
							getImagePreviews();
						}
					} else {
						console.warn(`Dropped file "${droppedFiles[i].name}" is not of an accepted type and was skipped.`);
					}
				}

				if (props.multiple === false || !props.preview) {
					submitFiles();
				}
			});
		});

		return {
			fileform,
			fileInput,
			previewRefs,
			dragAndDropCapable,
			files,
			uploadPercentage,
			uploadLabel,
			acceptedFileTypes,
			getDragText,
			getTitle,
			close,
			handleFileUpload,
			removeFile,
		};
	},
};
</script>
<style lang="less" scoped>
@import "./../assets/css/less/_settings.less";
@import "./../assets/css/less/_helper.less";
.uploader-content {
	margin: 10px 40px 0 0;
	form {
		display: block;
		background: @darkContrast;
		margin: 0 auto;
		text-align: center;
		height: 100px;
		.vertical-align-parent;

		input {
			width: 180px;
			color: #fff;
		}

		&.drag {
			height: 300px;
			border: 2px dashed #fff;

			label {
				margin-top: 10px;
				color: #fff;
			}
		}

		div,
		label {
			margin: 0 auto;
			display: block;
			color: #fff;
		}

		.progress-text {
			display: block;
			width: 100%;
			text-align: center;
			top: 60px;
			color: #fff;
			margin: 0;
			padding: 0;
			position: absolute;
			left: 0;
			font-size: 20px;
		}

		> div {
			width: 100%;
			.vertical-align;
			padding: 20px;

			&.progress {
				height: 20px;
				background: white;
				width: 80%;
				left: 10%;
				padding: 0;

				span {
					background: rgba(0, 0, 0, 0.4);
					height: 20px;
					display: block;
				}
			}
		}
		background: fade(@formDefaultColour, 30%) !important;
		label {
			font-weight: normal !important;
			color: @darkContrast !important;
		}
		div,
		input {
			color: @darkContrast !important;
		}
	}
}
.file-listing {
	display: flex;
	align-items: center;
	margin-bottom: 10px;
	.preview {
		width: 50px;
		height: 50px;
		object-fit: cover;
		margin-right: 10px;
		border: 1px solid #ccc;
	}
	.remove {
		margin-left: auto;
		color: red;
		cursor: pointer;
		padding: 5px;
	}
}
</style>